// novem dashboard — the Faz 0.3 GOLDEN SAMPLE (NovemHR-ekranlar
// dashboard.jsx port). This screen establishes the port pattern every
// Track A batch copies:
//
//   1. Mockup'ın GÖRSEL yapısı (PageHead serif selamlama + KPI grid +
//      bekleyen onaylar kartı + dağılım yan kartı) birebir taşınır.
//   2. Mockup'ın STATİK verisi yerine mevcut data hook'ları bağlanır —
//      burada leave requests (useKeysetQuery + approve/reject
//      mutations, LeaveListContent'le AYNI api fonksiyonları) +
//      employees summary + features/home-dashboard/widgets.tsx kartları.
//      Sözleşmesi olmayan çizim öğesi (hafta/ay farkı, ekip durumu) çizilmez.
//   3. Mockup'ın inline-style'ları Tailwind utility + semantik CSS
//      var'larına çevrilir (raw hex YASAK — spec 44 invariant).
//      Component kütüphanesinin novem skin'i (Card/StatCard/Badge/
//      PageHeader) yükü taşır; ekran yalnız kompozisyon + ekrana özgü
//      ince ayar yazar.
//   4. Mockup'ın Türkçe metinleri tr-TR.json'a kaynak olur; her string
//      t() üzerinden gelir (hardcoded literal YASAK — ADR-006).
//
// Dual-role: "/" employee shell'inde mount olur. Dashboard aggregates use the
// caller's server-resolved scope; a manager's distinct actionable approval card
// uses its current-assignment query below.
// Approve/reject aksiyonları LeaveListContent'teki capability gate'in aynısıyla
// gizlenir; sunucu her durumda gerçek otorite (ADR-017).

import { CalendarCheck2, CircleCheck, CircleX, Clock3, Users } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router";
import { useAuth } from "react-oidc-context";

import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  Empty,
  ErrorState,
  IconButton,
  Loading,
  StatCard,
} from "@novemai/components";

import { platformGraph } from "@/api/platform-graph";
import { unwrapEnvelope, type EnvelopedFetchResult, errorMessage } from "@/api/errors";
import { useKeysetQuery, type KeysetPage } from "@/api/keyset";
import { useScopeTier } from "@/api/use-scope-tier";
import { type LeaveRequestRow } from "@/features/leave/leave-api";
import {
  LEAVE_CAP_APPROVE,
  leaveCapabilities,
} from "@/features/leave/leave-capabilities";
import { LEAVE_STATUS_TONE, displayLeaveStatus } from "@/features/leave/leave-status";
import { useLeaveDecisions } from "@/features/leave/use-leave-decisions";
import { useEffectivePermissions } from "@/features/roles-permissions/use-effective-permissions";
import { formatDateRange } from "@/lib/date-range";
import { formatHours } from "@/lib/hours";
import { HomeDashboard, HomeQuickLinks, type HomeWidget } from "@/features/home-dashboard/home-dashboard";
import { HomeHeaderActions } from "@/features/home-dashboard/quick-actions";
import {
  hidesWidget,
  useAnnouncementsWidget,
  useApprovalQueueWidget,
  useLeaveBalanceWidget,
  useMyTasksWidget,
  useNextPayrollWidget,
  useRecentActivityWidget,
  useUpcomingHolidaysWidget,
} from "@/features/home-dashboard/widgets";
import { fetchEmployeeSummary } from "@/features/employees/api";
import { formatCountWithMore } from "@/lib/count";
import { PersonLink } from "@/components/person-link";

// ---- data (LeaveListContent'le aynı fetch contract'ları) ------------------

type LeaveGet = (
  path: "/v1/leave/requests",
  init: { params: { query: { cursor?: string; awaiting_my_decision?: boolean; mine?: boolean } } },
) => Promise<EnvelopedFetchResult<KeysetPage<LeaveRequestRow>>>;

async function fetchLeavePage(
  cursor: string | null,
  awaitingMyDecision = false,
  mine = false,
): Promise<KeysetPage<LeaveRequestRow>> {
  const get = platformGraph.GET as unknown as LeaveGet;
  return unwrapEnvelope(
    get("/v1/leave/requests", {
      params: {
        query: {
          ...(cursor !== null && { cursor }),
          ...(awaitingMyDecision && { awaiting_my_decision: true }),
          ...(mine && { mine: true }),
        },
      },
    }),
  );
}

// ---- helpers ---------------------------------------------------------------

// One card, two audiences: an approver is asked for a decision, everyone else is
// told what is still waiting on somebody else. Whole keys, not a suffix glued
// on, so a search for `dashboard.lead_self` finds its only consumer.
const PENDING_COPY_DECIDER = {
  lead: "dashboard.lead",
  leadMore: "dashboard.lead_more",
  title: "dashboard.pending_title",
  sub: "dashboard.pending_sub",
  subMore: "dashboard.pending_sub_more",
} as const;
const PENDING_COPY_SELF = {
  lead: "dashboard.lead_self",
  leadMore: "dashboard.lead_self_more",
  title: "dashboard.pending_title_self",
  sub: "dashboard.pending_sub_self",
  subMore: "dashboard.pending_sub_self_more",
} as const;

function greetingKey(hour: number): string {
  if (hour < 12) return "dashboard.greeting_morning";
  if (hour < 18) return "dashboard.greeting_afternoon";
  return "dashboard.greeting_evening";
}

export function DashboardPage(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const auth = useAuth();
  // Decision authority is PERMISSION-derived, never hat-derived: the dashboard
  // mounts on the employee-shell "/" too, and an approver there must still see
  // their inbox (the hat-gate regression showed "0 pending" with 3 waiting).
  // While permissions load the set is empty (default-closed) so canDecide
  // starts false and the inbox query stays idle; `enabled` below is
  // re-evaluated every render, so the query fires as soon as the resolved
  // permissions flip canDecide to true.
  const { capabilities: allowedCells } = useEffectivePermissions();
  const canDecide = leaveCapabilities(allowedCells).has(LEAVE_CAP_APPROVE);

  const scopedLeave = useKeysetQuery<LeaveRequestRow>({
    queryKey: ["leave", "requests", "dashboard-scope"],
    fetchPage: (cursor) => fetchLeavePage(cursor),
  });
  const actionableLeave = useKeysetQuery<LeaveRequestRow>({
    queryKey: ["leave", "requests", "awaiting-my-decision"],
    fetchPage: (cursor) => fetchLeavePage(cursor, true),
    enabled: canDecide,
  });
  // A caller with nothing to decide gets THEIR OWN pending requests in the
  // pending card (E37); a decider gets them in a separate card next to the
  // decision inbox (manager home, drawing 2b). The dashboard-scope query above
  // is the caller's whole visible scope, which for a wide-scope role includes
  // other people's requests, and "your requests" copy over those would be wrong.
  const ownLeave = useKeysetQuery<LeaveRequestRow>({
    queryKey: ["leave", "requests", "dashboard-mine"],
    fetchPage: (cursor) => fetchLeavePage(cursor, false, true),
  });
  const ownPending = ownLeave.items.filter((r) => r.status === "submitted");
  // employee_self için özet yalnız çağıranın kendi satırını sayar; şirket
  // personeli etiketi altında "1" yanlış olurdu (D3, 2026-08-03). /v1/me
  // çözülene kadar kapalı kalır ki çözülen employee_self gösterdiği sayıyı geri
  // almak zorunda kalmasın. Özet, listeyi sayfalayıp "N+" yazmak yerine kesin
  // toplamı verir.
  const scopeTier = useScopeTier();
  const showHeadcount = scopeTier !== undefined && scopeTier !== "employee_self";
  const headcount = useQuery({
    queryKey: ["employees", "summary"],
    queryFn: fetchEmployeeSummary,
    enabled: showHeadcount,
  });
  const activeHeadcount = headcount.data === undefined
    ? headcount.isError ? t("common.unknown") : "…"
    : String(headcount.data.by_lifecycle["active"] ?? 0);
  const extraWidgets = [
    useApprovalQueueWidget(),
    useLeaveBalanceWidget(),
    useMyTasksWidget(),
    useAnnouncementsWidget(),
    useNextPayrollWidget(),
    useUpcomingHolidaysWidget(),
    useRecentActivityWidget(),
  ].filter((widget): widget is HomeWidget => widget !== null);

  // Approve / reject open the shared confirmation dialog (reject asks for its
  // reason) and report their outcome with a toast; a failed decision shows its
  // error inside that dialog, so the icon-only buttons never fail silently.
  const decisions = useLeaveDecisions();

  // The dashboard's decision card is an actor-current inbox for approvers and
  // the caller's own pending requests for everyone else; status aggregates
  // remain on the caller's server-resolved dashboard scope.
  const pendingSource = canDecide ? actionableLeave : ownLeave;
  const pending = canDecide
    ? actionableLeave.items
    : ownPending;
  const copy = canDecide ? PENDING_COPY_DECIDER : PENDING_COPY_SELF;
  // Aggregates count the status a request is SHOWN as (displayLeaveStatus), the
  // same one the request list prints: an approved leave whose last day has passed
  // is "Kullanıldı" in the list, so counting it under "Onaylanan" here would make
  // one screen say two things about the same rows. The stored status is untouched.
  const shownStatuses = scopedLeave.items.map((r) => displayLeaveStatus(r));
  const approvedCount = shownStatuses.filter((s) => s === "approved").length;
  const rejectedCount = shownStatuses.filter((s) => s === "rejected").length;

  // Selamlama — OIDC profilinden ad; tarih kullanıcının diliyle formatlanır
  // (Intl — locale-specific string'i elle kurma).
  const firstName =
    auth.user?.profile.given_name ??
    auth.user?.profile.name?.split(" ")[0] ??
    auth.user?.profile.preferred_username ??
    "";
  const today = new Intl.DateTimeFormat(i18n.language, { dateStyle: "full" }).format(new Date());

  const distribution: readonly (readonly [string, number])[] = Object.entries(
    shownStatuses.reduce<Record<string, number>>((acc, status) => {
      acc[status] = (acc[status] ?? 0) + 1;
      return acc;
    }, {}),
  ).sort((a, b) => b[1] - a[1]);
  const distributionTotal = shownStatuses.length;

  const fmtRange = (startIso: string, endIso: string): string =>
    formatDateRange(i18n.language, startIso, endIso);

  const widgets: readonly HomeWidget[] = [
    { id: "pending_total", label: t("dashboard.kpi.pending"), content: (<StatCard
          label={t("dashboard.kpi.pending")}
          value={formatCountWithMore(pending.length, pendingSource.hasNextPage)}
          tone="warning"
          icon={<Clock3 size={18} />}
        />) },
    { id: "approved_total", label: t("dashboard.kpi.approved"), content: (<StatCard
          label={t("dashboard.kpi.approved")}
          value={formatCountWithMore(approvedCount, scopedLeave.hasNextPage)}
          tone="success"
          icon={<CircleCheck size={18} />}
        />) },
    ...(showHeadcount && !hidesWidget(headcount.error) ? [{ id: "employees_total", label: t("dashboard.kpi.employees"), content: (<StatCard
            label={t("dashboard.kpi.employees")}
            value={activeHeadcount}
            tone="info"
            icon={<Users size={18} />}
        />) } as HomeWidget] : []),
    { id: "rejected_total", label: t("dashboard.kpi.rejected"), content: (<StatCard
          label={t("dashboard.kpi.rejected")}
          value={formatCountWithMore(rejectedCount, scopedLeave.hasNextPage)}
          tone="neutral"
          icon={<CircleX size={18} />}
        />) },
    { id: "pending_requests", label: t(copy.title), content: (<Card data-tour="pending-approvals">
          <CardHeader>
            <div>
              <div className="nai-card-title">{t(copy.title)}</div>
              <div className="nai-card-subtitle">
                {pendingSource.hasNextPage
                  ? t(copy.subMore, { count: pending.length })
                  : t(copy.sub, { count: pending.length })}
              </div>
            </div>
            <RouterLink
              to={canDecide ? "/requests" : "/me/requests"}
              className="font-sans text-[13px] text-[var(--accent)] no-underline hover:underline"
            >
              {t("dashboard.view_all")}
            </RouterLink>
          </CardHeader>

          {pendingSource.isLoading ? (
            <Loading label={t("leave.loading")} rows={4} />
          ) : pendingSource.isError ? (
            <ErrorState
              title={t("leave.error_title")}
              description={errorMessage(pendingSource.error, t)}
            />
          ) : pending.length === 0 ? (
            <Empty
              title={t("dashboard.empty_pending_title")}
              description={t("dashboard.empty_pending_desc")}
            />
          ) : (
            <table className="w-full border-collapse">
              <tbody>
                {pending.map((r, i) => (
                  <tr
                    key={r.request_id}
                    className={i > 0 ? "border-t border-[var(--border-subtle)]" : ""}
                  >
                    <td className="px-[22px] py-[13px]">
                      <PersonLink employeeId={r.employee_id}>
                        <div className="font-serif text-[15px] text-[var(--fg-primary)]">
                          {/* SEM-301: resolved subject name; short id as fallback. */}
                          {r.employee_name ?? r.employee_id.slice(0, 8)}
                        </div>
                      </PersonLink>
                      {r.reason !== null && (
                        <div className="truncate font-sans text-[11.5px] text-[var(--fg-muted)]">
                          {r.reason}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-[13px]">
                      <Badge tone={LEAVE_STATUS_TONE[r.status] ?? "neutral"}>
                        {t(`leave.status.${r.status}`)}
                      </Badge>
                    </td>
                    <td className="whitespace-nowrap px-2 py-[13px] font-sans text-[13px] font-medium text-[var(--fg-secondary)]">
                      {t("leave.list.hours_value", {
                        hours: formatHours(r.hours, i18n.language) ?? t("common.unknown"),
                      })}
                      <div className="text-[11.5px] font-normal text-[var(--fg-muted)]">
                        {fmtRange(r.start_date, r.end_date)}
                      </div>
                    </td>
                    {canDecide && (
                      <td className="px-[22px] py-[13px] text-right">
                        <div className="flex justify-end gap-1.5">
                          <IconButton
                            aria-label={t("dashboard.reject")}
                            onClick={(): void => {
                              decisions.ask("reject", r);
                            }}
                          >
                            <CircleX size={15} />
                          </IconButton>
                          <IconButton
                            aria-label={t("dashboard.approve")}
                            className="!bg-[var(--accent)] !text-[var(--fg-on-accent)] hover:!bg-[var(--accent-hover)]"
                            onClick={(): void => {
                              decisions.ask("approve", r);
                            }}
                          >
                            <CircleCheck size={15} />
                          </IconButton>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>) },
    ...(canDecide ? [{ id: "my_requests", label: t(PENDING_COPY_SELF.title), content: (<Card>
          <CardHeader>
            <div>
              <div className="nai-card-title">{t(PENDING_COPY_SELF.title)}</div>
              <div className="nai-card-subtitle">
                {ownLeave.hasNextPage
                  ? t(PENDING_COPY_SELF.subMore, { count: ownPending.length })
                  : t(PENDING_COPY_SELF.sub, { count: ownPending.length })}
              </div>
            </div>
            <RouterLink to="/me/requests" className="font-sans text-[13px] text-[var(--accent)] no-underline hover:underline">
              {t("dashboard.view_all")}
            </RouterLink>
          </CardHeader>
          {ownLeave.isLoading ? (
            <Loading label={t("leave.loading")} rows={2} />
          ) : ownLeave.isError ? (
            <ErrorState title={t("leave.error_title")} description={errorMessage(ownLeave.error, t)} />
          ) : ownPending.length === 0 ? (
            <Empty title={t("dashboard.empty_pending_title")} description={t("dashboard.empty_pending_desc")} />
          ) : (
            <ul className="m-0 list-none p-0">
              {ownPending.map((r) => (
                <li key={r.request_id} className="flex items-center gap-3 border-t border-[var(--border-subtle)] px-[22px] py-3 first:border-t-0">
                  <span className="min-w-0 flex-1 font-sans text-[13px] text-[var(--fg-primary)]">
                    {fmtRange(r.start_date, r.end_date)}
                    <span className="block text-[11.5px] text-[var(--fg-muted)]">
                      {t("leave.list.hours_value", { hours: formatHours(r.hours, i18n.language) ?? t("common.unknown") })}
                    </span>
                  </span>
                  <Badge tone={LEAVE_STATUS_TONE[r.status] ?? "neutral"}>{t(`leave.status.${r.status}`)}</Badge>
                </li>
              ))}
            </ul>
          )}
        </Card>) } as HomeWidget] : []),
    { id: "leave_distribution", label: t("dashboard.distribution_title"), content: (<Card>
          <CardHeader>
            <div>
              <div className="nai-card-title">{t("dashboard.distribution_title")}</div>
              <div className="nai-card-subtitle">{t("dashboard.distribution_sub")}</div>
            </div>
            <CalendarCheck2 size={16} className="text-[var(--fg-muted)]" aria-hidden="true" />
          </CardHeader>
          <CardBody>
            {distributionTotal === 0 ? (
              <p className="font-sans text-[13px] text-[var(--fg-muted)]">
                {t("dashboard.empty_distribution_desc")}
              </p>
            ) : (
              <ul className="m-0 flex list-none flex-col gap-3 p-0">
                {distribution.map(([status, count]) => (
                  <li key={status}>
                    <div className="mb-1 flex items-center justify-between font-sans text-[12.5px]">
                      <span className="text-[var(--fg-secondary)]">
                        {t(`leave.status.${status}`)}
                      </span>
                      <span className="font-semibold text-[var(--fg-primary)]">{count}</span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-[var(--bg-subtle)]">
                      <div
                        className="h-full rounded-full bg-[var(--color-accent-400)]"
                        style={{ width: `${Math.round((count / distributionTotal) * 100)}%` }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>) },
    ...extraWidgets,
    { id: "quick_links", label: t("home_dashboard.quick_links"), content: <HomeQuickLinks /> },
  ];

  return (
    <div className="mx-auto max-w-[1320px]">
      <HomeDashboard
        title={
          <>
            {t(greetingKey(new Date().getHours()))}{" "}
            {firstName !== "" && <em>{firstName}.</em>}
          </>
        }
        description={
          pending.length > 0
            ? pendingSource.hasNextPage
              ? t(copy.leadMore, {
                  date: today,
                  count: pending.length,
                })
              : t(copy.lead, {
                  date: today,
                  count: pending.length,
                })
            : t("dashboard.lead_none", { date: today })
        }
        widgets={widgets}
        primaryAction={<HomeHeaderActions />}
      />
      {decisions.layer}
    </div>
  );
}
